import Youtube from '@tiptap/extension-youtube';
import {
  mergeAttributes,
  NodeViewRenderer,
  NodeViewRendererProps,
} from '@tiptap/core';
import { Node } from '@tiptap/pm/model';

/**
 * 유튜브 URL을 항상 embed 가능한 URL 형식으로 변환합니다.
 */
const getEmbedUrl = (url: string): string => {
  if (!url) return '';
  if (url.includes('youtube.com/embed/')) return url;

  let videoId = '';
  try {
    if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1].split(/[?#]/)[0];
    } else if (url.includes('youtube.com/watch')) {
      videoId = url.split('v=')[1].split(/[&?#]/)[0];
    } else if (url.includes('youtube.com/live/')) {
      videoId = url.split('live/')[1].split(/[?#]/)[0];
    } else if (url.includes('youtube.com/shorts/')) {
      videoId = url.split('shorts/')[1].split(/[?#]/)[0];
    }
  } catch (e) {
    console.error('YouTube URL parsing error:', e);
  }

  return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
};

export const CustomYoutube = Youtube.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      isShorts: {
        default: false,
        parseHTML: element => element.getAttribute('data-is-shorts') === 'true',
        renderHTML: attributes => ({
          'data-is-shorts': attributes['isShorts'] ? 'true' : 'false',
        }),
      },
      textAlign: {
        default: 'left',
        renderHTML: attributes => ({
          'data-text-align': attributes['textAlign'],
        }),
      },
    };
  },

  renderHTML({ node, HTMLAttributes }) {
    const textAlign = node.attrs['textAlign'] || 'left';
    const isShorts = node.attrs['isShorts'] === true;
    const embedUrl = getEmbedUrl(HTMLAttributes['src'] || '');

    // width, height를 제외한 속성만 사용 (CSS로 처리)
    const {
      width: _width,
      height: _height,
      ...restAttributes
    } = HTMLAttributes;
    const iframeAttrs = mergeAttributes(this.options.HTMLAttributes, {
      ...restAttributes,
      src: embedUrl,
      allowfullscreen: 'true',
    });
    // mergeAttributes 결과에서도 width/height 제거
    delete iframeAttrs['width'];
    delete iframeAttrs['height'];

    return [
      'div',
      {
        'data-youtube-video': '',
        'data-text-align': textAlign,
        'data-is-shorts': isShorts ? 'true' : 'false',
      },
      ['iframe', iframeAttrs],
    ];
  },

  addNodeView(): NodeViewRenderer {
    return (props: NodeViewRendererProps) => {
      const { node, HTMLAttributes } = props;
      const container = document.createElement('div');
      container.classList.add('custom-youtube-container');
      container.setAttribute('data-youtube-video', '');

      const updateStyles = (n: Node) => {
        const align = n.attrs['textAlign'] || 'left';
        const isShorts = n.attrs['isShorts'] === true;
        container.setAttribute('data-text-align', align);
        container.setAttribute('data-is-shorts', isShorts ? 'true' : 'false');
      };

      updateStyles(node);

      const iframe = document.createElement('iframe');
      const finalAttributes = mergeAttributes(
        this.options.HTMLAttributes,
        HTMLAttributes
      );

      // width, height는 CSS로 처리하므로 속성에서 제외
      const skipAttrs = ['width', 'height'];
      Object.entries(finalAttributes).forEach(([key, value]) => {
        if (skipAttrs.includes(key)) return; // width/height 제외
        if (key === 'src') {
          iframe.setAttribute(key, getEmbedUrl(value as string));
        } else {
          iframe.setAttribute(key, value as string);
        }
      });
      iframe.style.pointerEvents = 'none';

      const overlay = document.createElement('div');
      overlay.classList.add('youtube-overlay');

      container.appendChild(iframe);
      container.appendChild(overlay);

      return {
        dom: container,
        selectNode: () => container.classList.add('ProseMirror-selectednode'),
        deselectNode: () =>
          container.classList.remove('ProseMirror-selectednode'),
        update: updatedNode => {
          if (updatedNode.type.name !== this.name) return false;
          updateStyles(updatedNode);
          return true;
        },
      };
    };
  },
});
